/**
 * Contrato e consultas do motor de formulário (spec §2.3).
 *
 * Três regras moram aqui, e nenhuma delas é detalhe de serialização:
 *
 * · **A tela não decide o que aparece.** Visibilidade, obrigatoriedade efetiva, opção escondida,
 *   progresso e impedimento de envio chegam JÁ AVALIADOS do servidor. O inventário mediu o preço
 *   de a regra viver no JavaScript: 11 ramificações existem só em prosa no enunciado e o
 *   resultado foi 44 de 44 clientes respondendo uma pergunta que valia para 32. Se esta camada
 *   recalculasse condicional, a próxima tela (revisão, pacote) voltaria a divergir da primeira.
 *
 * · **O valor é sempre um envelope de chaves nomeadas, nunca um escalar.** É a mesma regra que o
 *   `CHECK ck_resposta_valor_e_objeto` cobra no banco. É ela que impede a volta da concatenação
 *   por vírgula: escolha múltipla grava `{opcoes: [...], outro: "..."}` em campos separados,
 *   porque o texto do "Outro" também tem vírgula. Os acessores e construtores de envelope estão
 *   aqui para que nenhuma tela precise montar esse objeto à mão.
 *
 * · **Envelope vazio apaga a resposta.** `null` no corpo do rascunho é o que permite desmarcar
 *   uma escolha sem inventar um valor de significado ambíguo — e é o que mantém honesta a conta
 *   de "respondidas". Os construtores devolvem `null` quando o conteúdo não sobrou nada, com a
 *   mesma regra de vazio que o servidor aplica (`esta_vazio`).
 *
 * Nenhuma chamada daqui recebe id de implantação. A implantação sai da organização da sessão, no
 * servidor, exatamente como em `/implantacoes/minha`: sem parâmetro não há o que forjar.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { ancoraDaPergunta } from "../componentes/kickoff/ancora";
import { BASE_DA_API, ErroDaApi, apagar, atualizarJson, enviarFormulario, enviarJson, pedirJson } from "./api";

/* ------------------------------------------------------------------ vocabulário */

/**
 * Os dezesseis tipos de campo do motor.
 *
 * Os quinze primeiros são os da spec §2.3 e nenhum foi inventado: todos saíram do inventário dos
 * 581 kickoffs. `credencial` é a exceção declarada — ele não veio de medição, veio do cofre
 * (spec §2.4), e existe porque a alternativa era guardar a senha do banco do ERP em
 * `resposta.valor`, que é jsonb **em claro**. É o único tipo cujo valor não é o que a pessoa
 * digitou: o que fica gravado é `{"segredo_id": "..."}`, a referência ao segredo cifrado.
 *
 * Faltar `credencial` nesta lista não daria um campo feio — daria a trilha inteira de
 * infraestrutura fora do ar: `esquemaTipoDePergunta` reprovaria a pergunta, o erro subiria por
 * `esquemaFormularioDaTrilha` e `/app/formularios/infraestrutura` mostraria "A resposta da API
 * não bate com o contrato." em vez do formulário.
 */
export const TIPOS_DE_PERGUNTA = [
  "texto_curto",
  "texto_longo",
  "sim_nao",
  "escolha_unica",
  "escolha_multipla",
  "outro",
  "contato",
  "lista_repetivel",
  "upload",
  "cor",
  "fonte",
  "galeria",
  "url",
  "numero",
  "bloco_informativo",
  "credencial",
] as const;

const esquemaTipoDePergunta = z.enum(TIPOS_DE_PERGUNTA);
const esquemaEscopo = z.enum(["modulo", "derivada", "cadastro", "infraestrutura"]);
const esquemaEstadoDaSessao = z.enum(["nao_iniciada", "em_preenchimento", "enviada"]);
const esquemaMotivo = z.enum([
  "obrigatoria_sem_resposta",
  "promessa_de_anexo_sem_anexo",
  "valor_invalido",
]);

export type TipoDePergunta = z.infer<typeof esquemaTipoDePergunta>;
export type EscopoDaTrilha = z.infer<typeof esquemaEscopo>;
export type EstadoDaSessao = z.infer<typeof esquemaEstadoDaSessao>;
export type MotivoDeImpedimento = z.infer<typeof esquemaMotivo>;

/** A trilha que a spec §4 governa por `infra:*` em vez de `kickoff:*`. */
export const TRILHA_DE_INFRAESTRUTURA = "infraestrutura";

/* ------------------------------------------------------------------- esquemas */

const esquemaPessoa = z.object({
  id: z.string().min(1),
  nome: z.string().min(1),
  email: z.string().min(1),
});

const esquemaProgresso = z.object({
  perguntas_visiveis: z.number(),
  respondidas: z.number(),
  obrigatorias_visiveis: z.number(),
  obrigatorias_respondidas: z.number(),
  /* Mede as OBRIGATÓRIAS e só chega a 100 com a trilha enviada (spec de 28/09, 6.2). Quem
     calcula é o servidor; cliente e console só leem — dois cálculos voltariam a discordar. */
  percentual: z.number(),
  /** Enviada com todas as obrigatórias respondidas — o "Concluído" da coluna de status. */
  concluida: z.boolean(),
});

const esquemaSessaoDaTrilha = z.object({
  id: z.string().nullable(),
  estado: esquemaEstadoDaSessao,
  responsavel: esquemaPessoa.nullable(),
  prazo_em: z.string().nullable(),
  iniciada_em: z.string().nullable(),
  enviada_em: z.string().nullable(),
  congelada: z.boolean(),
});

const esquemaTrilhaDaImplantacao = z.object({
  chave: z.string().min(1),
  nome: z.string().min(1),
  escopo: esquemaEscopo,
  origem: z.string().min(1),
  versao: z.number(),
  questionario_versao_id: z.string().min(1),
  sessao: esquemaSessaoDaTrilha,
  progresso: esquemaProgresso,
  pode_responder: z.boolean(),
});

export const esquemaRespostaDasTrilhas = z.object({
  implantacao_id: z.string().min(1),
  trilhas: z.array(esquemaTrilhaDaImplantacao),
  total: z.number(),
  enviadas: z.number(),
});

const esquemaOpcaoDaPergunta = z.object({
  id: z.string().min(1),
  rotulo: z.string().min(1),
  ajuda: z.string().nullish(),
  /* A galeria mostra imagem quando o domínio a publica. Hoje `layout_b2b` está vazio de
     propósito — é pendência declarada do design, não esquecimento —, então o campo é opcional
     e a tela cai no cartão só com rótulo em vez de quebrar. */
  imagem: z.string().nullish(),
});

const esquemaCampoDeclarado = z.object({
  id: z.string().min(1),
  rotulo: z.string().min(1),
  /* O tipo do SUBCAMPO. Não é decoração: `crm.participantes_dos_encontros` declara `encontro`
     como `escolha_unica` com três opções e `an.exemplo_de_grupo_economico` declara `principal`
     como `sim_nao`. Sem `opcoes` aqui, a lista era descartada no parse e a tela não tinha como
     renderizar a escolha — o subcampo virava texto livre, que é o defeito que o tipo de campo
     veio matar (100 grafias distintas em 132 respostas). O servidor confere os dois. */
  tipo: z.string().optional(),
  opcoes: z.array(esquemaOpcaoDaPergunta).optional(),
  formato: z.string().optional(),
  obrigatorio: z.boolean().optional(),
});

/* A `config` da versão da pergunta. Chave desconhecida é descartada pelo zod, o que é o
   comportamento certo: o catálogo carrega marcas que só interessam ao pacote e à revisão
   (`destaque_no_pacote`, `bloqueia_promessa_de_anexo`) e a tela não tem o que fazer com elas. */
const esquemaConfigDaPergunta = z.object({
  somente_arquivo: z.boolean().optional(),
  orientacao: z.string().optional(),
  placeholder: z.string().optional(),
  formato: z.string().optional(),
  tabela: z.object({ colunas: z.array(z.string()), linhas: z.array(z.array(z.string())) }).optional(),
  links: z.array(z.object({ rotulo: z.string(), url: z.string().url() })).optional(),
  opcoes: z.array(esquemaOpcaoDaPergunta).optional(),
  campos: z.array(esquemaCampoDeclarado).optional(),
  permite_outro: z.boolean().optional(),
  aceita_anexo: z.boolean().optional(),
  aceita_texto_livre: z.boolean().optional(),
  formatos: z.array(z.string()).optional(),
  tamanho_maximo_mb: z.number().optional(),
  minimo: z.number().optional(),
  maximo: z.number().optional(),
  unidade: z.string().optional(),
  /* O default confirmável. A LISTA é o caso real e era o que derrubava a trilha inteira:
     `b2b.publico_do_b2b` declara `padrao: ["lojista_cnpj"]` (100% das 44 respostas medidas) e a
     união sem `array` reprovava a config — `padrao` é chave conhecida, então o zod não a
     descartava, ele falhava. O erro subia por `esquemaPergunta` → `esquemaFormularioDaTrilha` e
     `/app/formularios/b2b` mostrava "A resposta da API não bate com o contrato." em vez do
     formulário. `configs-do-catalogo.json` existe para que isso reprove aqui, e não no cliente. */
  padrao: z.union([z.boolean(), z.string(), z.number(), z.array(z.string())]).optional(),
  confirmar_padrao: z.boolean().optional(),
  dominio: z.string().optional(),
  procedencia: z.string().optional(),
  /* Como o segredo desta pergunta é reconhecido na tela do cofre, já que o valor nunca aparece
     lá. Vem do catálogo (`rotulo_do_segredo`), não do que a pessoa digita. */
  rotulo_do_segredo: z.string().optional(),
  /* A leitura MASCARADA da credencial guardada — o que dá para dizer sobre ela sem dizer o que
     ela é. Chega na `config` e não ao lado do valor porque `ValorDaResposta` é contrato da F5 e
     esta fatia não o reabriu; o lugar definitivo é lá, e isso está declarado como dívida no
     `_config_com_a_credencial` do servidor.

     `custodia_provisoria` aparece para o PRÓPRIO cliente de propósito: quem entregou a senha é
     quem mais merece saber que ela está sob a chave de emergência do host, e não sob a do
     serviço gerenciado. */
  segredo: z
    .object({
      guardado: z.boolean(),
      rotulo: z.string().nullish(),
      guardado_em: z.string().nullish(),
      custodia: z.string().nullish(),
      custodia_provisoria: z.boolean(),
      expira_em: z.string().nullish(),
      descartado_em: z.string().nullish(),
    })
    .optional(),
});

export type ConfigDaPergunta = z.infer<typeof esquemaConfigDaPergunta>;
export type OpcaoDaPergunta = z.infer<typeof esquemaOpcaoDaPergunta>;
export type CampoDeclarado = z.infer<typeof esquemaCampoDeclarado>;

const esquemaCondicional = z.object({
  gatilho_pergunta_id: z.string().nullable(),
  gatilho_externo: z.string().nullable(),
  operador: z.string(),
  valor: z.unknown().nullable(),
  /* `null` é a condição que o portal ainda não tem como resolver — o dado externo não existe em
     tabela nenhuma. Nesse caso a pergunta aparece e deixa de ser obrigatória: esconder perderia
     dado e cobrar cobraria o que talvez não se aplique. */
  satisfeita: z.boolean().nullable(),
  explicacao: z.string(),
});

/* O envelope do valor. Deliberadamente permissivo: uma resposta gravada por uma versão anterior
   do catálogo não pode derrubar a tela inteira por causa de uma chave a mais. Quem dá sentido a
   cada chave são os acessores mais abaixo, que tratam o inesperado como ausente. */
const esquemaEnvelope = z.record(z.string(), z.unknown());

const esquemaValorDaResposta = z.object({
  valor: esquemaEnvelope,
  herdada_de_pergunta_id: z.string().nullable(),
  herdada_de_trilha: z.string().nullable(),
  /* `true` = o motor está OFERECENDO o valor de uma pergunta equivalente e nada foi gravado.
     Sugestão não confirmada não conta como resposta em lugar nenhum — nem no progresso, nem no
     envio —, e confirmar é salvar o mesmo valor. */
  sugerida: z.boolean(),
  confirmada_em: z.string().nullable(),
  respondido_em: z.string().nullable(),
  respondido_por: esquemaPessoa.nullable(),
});

const esquemaAnexo = z.object({
  id: z.string().min(1),
  pergunta_id: z.string().min(1),
  nome_original: z.string().min(1),
  mime: z.string(),
  tamanho: z.number(),
  enviado_em: z.string(),
  enviado_por: esquemaPessoa.nullable(),
  verificado_em: z.string().nullable(),
});

const esquemaPergunta = z.object({
  id: z.string().min(1),
  ordem: z.number(),
  tipo: esquemaTipoDePergunta,
  enunciado: z.string().min(1),
  ajuda: z.string().nullable(),
  obrigatoria: z.boolean(),
  obrigatoria_no_catalogo: z.boolean(),
  config: esquemaConfigDaPergunta,
  visivel: z.boolean(),
  condicional: esquemaCondicional.nullable(),
  opcoes_ocultas: z.array(z.string()),
  /* `true` = pergunta de escolha cujo domínio ainda não publicou opção nenhuma
     (`b2b.layout_preferido` lendo `layout_b2b`). O servidor deixa de exigi-la; a tela precisa
     dizer por que ela está vazia — a reclamação medida é exatamente essa: 6 dos 28 escreveram
     "não nos mostraram quais os layouts possiveis". */
  sem_opcoes_publicadas: z.boolean(),
  aceita_anexo: z.boolean(),
  resposta: esquemaValorDaResposta.nullable(),
  anexos: z.array(esquemaAnexo),
});

const esquemaImpedimento = z.object({
  pergunta_id: z.string().min(1),
  enunciado: z.string().min(1),
  motivo: esquemaMotivo,
  detalhe: z.string().min(1),
});

export const esquemaFormularioDaTrilha = z.object({
  implantacao_id: z.string().min(1),
  chave: z.string().min(1),
  nome: z.string().min(1),
  escopo: esquemaEscopo,
  origem: z.string().min(1),
  versao: z.number(),
  questionario_versao_id: z.string().min(1),
  notas: z.string().nullable(),
  sessao: esquemaSessaoDaTrilha,
  perguntas: z.array(esquemaPergunta),
  progresso: esquemaProgresso,
  impedimentos: z.array(esquemaImpedimento),
  pode_responder: z.boolean(),
  pode_anexar: z.boolean().optional(),
});

/* A revisão do lado interno. É a metade que faltava da regra da contradição: o motor a detecta
   desde a F5, e ela saía só por `GET /api/implantacoes/{id}/kickoff` — sem tela nenhuma, o
   consultor precisava de `curl` para ver o que a spec §2.3 manda o portal APRESENTAR a ele. O
   caso que originou a regra (ONB-3114 negou controle de estoque no Força de Vendas e, 30 minutos
   depois, descreveu controle de estoque por integração no formulário de Estoque) continuava
   invisível na prática. */
const esquemaContradicao = z.object({
  estado: z.enum(["divergente", "nao_comparavel"]),
  pergunta_a: z.string().min(1),
  trilha_a: z.string().min(1),
  nome_da_trilha_a: z.string().min(1),
  enunciado_a: z.string().min(1),
  valor_a: esquemaEnvelope,
  /* A resposta já em texto de gente — rótulo da opção, "Sim"/"Não" —, traduzida pelo servidor
     com o catálogo de cada pergunta. É ela que a tela mostra, nunca o envelope (spec 6.8). */
  resposta_a: z.string(),
  pergunta_b: z.string().min(1),
  trilha_b: z.string().min(1),
  nome_da_trilha_b: z.string().min(1),
  enunciado_b: z.string().min(1),
  valor_b: esquemaEnvelope,
  resposta_b: z.string(),
  nota: z.string().nullable(),
});

export const esquemaRevisaoDoKickoff = z.object({
  implantacao_id: z.string().min(1),
  organizacao: z.object({
    id: z.string().min(1),
    razao_social: z.string().min(1),
    nome_fantasia: z.string().nullable(),
    cnpj: z.string().min(1),
    cidade: z.string().nullable(),
    uf: z.string().nullable(),
  }),
  trilhas: z.array(esquemaFormularioDaTrilha),
  contradicoes: z.array(esquemaContradicao),
  /* Módulo contratado sobre o qual o kickoff não perguntou nada — `catalogo_digital` não tem
     questionário no catálogo v1 e `guiarep` nasce despublicado. Sem esta lista, o cliente
     responde tudo o que lhe foi oferecido, a esteira anuncia conclusão e a lacuna some. */
  modulos_sem_trilha: z.array(z.object({ chave: z.string().min(1), nome: z.string().min(1) })),
  /* O aceite que JÁ existe, lido da linha do tempo. Sem este campo o cartão do aceite derivava
     "já aceitei" do resultado da MUTAÇÃO — que vive só enquanto o componente vive. Um `F5` e o
     console voltava a oferecer o ato; outro consultor abrindo a mesma implantação nunca via que
     alguém já tinha aceitado. `null` é "ninguém aceitou ainda". */
  aceite: z
    .object({
      aceito_em: z.string().min(1),
      aceito_por: z.string().min(1),
      trilhas_enviadas: z.array(z.string()),
      trilhas_pendentes: z.array(z.string()),
      contradicoes: z.number(),
      observacao: z.string().nullable(),
    })
    .nullable(),
  total: z.number(),
  enviadas: z.number(),
  em_preenchimento: z.number(),
  nao_iniciadas: z.number(),
});

export type ContradicaoEntreTrilhas = z.infer<typeof esquemaContradicao>;
export type AceiteRegistrado = NonNullable<RevisaoDoKickoff["aceite"]>;
export type RevisaoDoKickoff = z.infer<typeof esquemaRevisaoDoKickoff>;

export type RespostaDasTrilhas = z.infer<typeof esquemaRespostaDasTrilhas>;
export type TrilhaDaImplantacao = z.infer<typeof esquemaTrilhaDaImplantacao>;
export type FormularioDaTrilha = z.infer<typeof esquemaFormularioDaTrilha>;
export type PerguntaDoFormulario = z.infer<typeof esquemaPergunta>;
export type ProgressoDaTrilha = z.infer<typeof esquemaProgresso>;
export type SessaoDaTrilha = z.infer<typeof esquemaSessaoDaTrilha>;
export type AnexoDaResposta = z.infer<typeof esquemaAnexo>;
export type ImpedimentoDeEnvio = z.infer<typeof esquemaImpedimento>;
export type ValorDaResposta = z.infer<typeof esquemaValorDaResposta>;

/* ------------------------------------------------------- envelope: leitura e escrita */

/** O valor de uma resposta: objeto de chaves nomeadas, do jeito que o banco guarda. */
export type ValorDaPergunta = Record<string, unknown>;

function comoTexto(bruto: unknown): string {
  return typeof bruto === "string" ? bruto : "";
}

export function textoDo(valor: ValorDaPergunta | null): string {
  return valor ? comoTexto(valor.texto) : "";
}

/** `null` é "ainda não respondeu" — diferente de `false`, que é uma resposta. */
export function booleanoDo(valor: ValorDaPergunta | null): boolean | null {
  const bruto = valor?.booleano;
  return typeof bruto === "boolean" ? bruto : null;
}

export function numeroDo(valor: ValorDaPergunta | null): number | null {
  const bruto = valor?.numero;
  return typeof bruto === "number" && Number.isFinite(bruto) ? bruto : null;
}

export function opcoesDo(valor: ValorDaPergunta | null): string[] {
  const bruto = valor?.opcoes;
  if (!Array.isArray(bruto)) return [];
  return bruto.filter((item): item is string => typeof item === "string");
}

/** O "Outro" é campo próprio: ele nunca é lido de dentro da lista de opções marcadas. */
export function outroDo(valor: ValorDaPergunta | null): string {
  return valor ? comoTexto(valor.outro) : "";
}

function comoRegistro(bruto: unknown): Record<string, string> {
  if (typeof bruto !== "object" || bruto === null || Array.isArray(bruto)) return {};
  const saida: Record<string, string> = {};
  for (const [chave, conteudo] of Object.entries(bruto)) {
    if (typeof conteudo === "string") saida[chave] = conteudo;
  }
  return saida;
}

export function camposDo(valor: ValorDaPergunta | null): Record<string, string> {
  return comoRegistro(valor?.campos);
}

export function itensDo(valor: ValorDaPergunta | null): Array<Record<string, string>> {
  const bruto = valor?.itens;
  if (!Array.isArray(bruto)) return [];
  return bruto.map(comoRegistro);
}

/**
 * A referência ao segredo de uma pergunta de credencial — `{"segredo_id": "<uuid>"}`.
 *
 * É o ÚNICO conteúdo que uma resposta de credencial carrega, e é o que permite à tela oferecer a
 * revelação sem nunca ter tido a senha: o que viaja pelo formulário é o ponteiro, não o valor.
 * Não existe construtor de envelope para este tipo, e a falta é deliberada — quem escreve
 * `resposta.valor` de uma pergunta de cofre é `cofre/servico.py::guardar_credencial`, e mais
 * ninguém; o salvamento de rascunho recusa qualquer outro conteúdo ali.
 */
export function referenciaDoSegredo(valor: ValorDaPergunta | null): string | null {
  const bruto = valor?.segredo_id;
  return typeof bruto === "string" && bruto.length > 0 ? bruto : null;
}

/** O que se sabe sobre a credencial guardada de uma pergunta — nunca o valor. */
export type SegredoNaPergunta = NonNullable<ConfigDaPergunta["segredo"]>;

/**
 * A mesma regra de vazio do servidor (`esta_vazio`): texto em branco, lista vazia e objeto vazio
 * não são resposta. `false` e `0` **são** — trocá-los por vazio apagaria a resposta de quem
 * respondeu "não".
 */
export function envelopeVazio(valor: ValorDaPergunta | null): boolean {
  if (!valor) return true;
  for (const conteudo of Object.values(valor)) {
    if (typeof conteudo === "string") {
      if (conteudo.trim()) return false;
    } else if (Array.isArray(conteudo)) {
      if (conteudo.length > 0) return false;
    } else if (typeof conteudo === "object" && conteudo !== null) {
      if (Object.keys(conteudo).length > 0) return false;
    } else if (conteudo !== null && conteudo !== undefined) {
      return false;
    }
  }
  return true;
}

/* Os construtores devolvem `null` quando não sobrou conteúdo, e `null` no corpo do rascunho é o
   que APAGA a resposta no servidor. É por isso que limpar um campo na tela não deixa para trás
   um `{"texto": ""}` que contaria como respondido. */
function ouVazio(valor: ValorDaPergunta): ValorDaPergunta | null {
  return envelopeVazio(valor) ? null : valor;
}

export function envelopeDeTexto(texto: string): ValorDaPergunta | null {
  return ouVazio({ texto });
}

export function envelopeDeBooleano(booleano: boolean | null): ValorDaPergunta | null {
  return booleano === null ? null : { booleano };
}

export function envelopeDeNumero(numero: number | null): ValorDaPergunta | null {
  return numero === null || !Number.isFinite(numero) ? null : { numero };
}

/** Opções marcadas e "Outro" em chaves separadas — nunca no mesmo campo, nunca por vírgula. */
export function envelopeDeEscolha(opcoes: string[], outro: string): ValorDaPergunta | null {
  const escolhido: ValorDaPergunta = { opcoes };
  if (outro.trim()) escolhido.outro = outro;
  return ouVazio(escolhido);
}

export function envelopeDeCampos(campos: Record<string, string>): ValorDaPergunta | null {
  const preenchidos = Object.fromEntries(
    Object.entries(campos).filter(([, conteudo]) => conteudo.trim()),
  );
  return ouVazio({ campos: preenchidos });
}

export function envelopeDeItens(
  itens: Array<Record<string, string>>,
): ValorDaPergunta | null {
  const limpos = itens
    .map((item) =>
      Object.fromEntries(Object.entries(item).filter(([, conteudo]) => conteudo.trim())),
    )
    .filter((item) => Object.keys(item).length > 0);
  return ouVazio({ itens: limpos });
}

/* ------------------------------------------------------------------- consultas */

export const CHAVE_DAS_TRILHAS = ["kickoff", "trilhas"] as const;

/**
 * A raiz de todas as chaves do motor: índice, formulário e revisão.
 *
 * Existe para o campo de credencial poder invalidar as três de uma vez. A gravação de uma senha
 * não passa pelo motor — ela vai por `PUT /cofre/credenciais/{pergunta}` —, mas muda a resposta
 * daquela pergunta, o progresso da trilha e o que o consultor lê na revisão. Invalidar só o
 * formulário aberto deixaria as outras duas telas contando que o campo continua vazio.
 */
export const CHAVE_RAIZ_DO_KICKOFF = ["kickoff"] as const;

export function chaveDaTrilha(chave: string) {
  return ["kickoff", "trilha", chave] as const;
}

/**
 * O formulário de uma trilha, ou o motivo de ele não estar disponível.
 *
 * `404` **não é falha**: é a trilha que não foi liberada para esta implantação — a de
 * infraestrutura, por exemplo, que o catálogo v1 declara como não publicada. A mensagem vem do
 * servidor porque é lá que ela está escrita para quem está do outro lado da tela.
 */
export type ResultadoDaTrilha =
  | { disponivel: true; formulario: FormularioDaTrilha }
  | { disponivel: false; motivo: string };

export interface RespostaParaGravar {
  pergunta_id: string;
  /** `null` apaga a resposta. */
  valor: ValorDaPergunta | null;
}

function caminhoDaApi(chave: string, sufixo = ""): string {
  return `/kickoff/trilhas/${encodeURIComponent(chave)}${sufixo}`;
}

/** As trilhas da própria implantação. `null` = a organização ainda não tem esteira aberta. */
export function useTrilhas(habilitada = true) {
  return useQuery<RespostaDasTrilhas | null>({
    enabled: habilitada,
    queryKey: CHAVE_DAS_TRILHAS,
    queryFn: async ({ signal }) => {
      try {
        return await pedirJson("/kickoff/trilhas", esquemaRespostaDasTrilhas, { signal });
      } catch (causa) {
        if (causa instanceof ErroDaApi && causa.status === 404) return null;
        throw causa;
      }
    },
    retry: false,
  });
}

export function useTrilha(chave: string) {
  return useQuery<ResultadoDaTrilha>({
    queryKey: chaveDaTrilha(chave),
    queryFn: async ({ signal }) => {
      try {
        const formulario = await pedirJson(
          caminhoDaApi(chave),
          esquemaFormularioDaTrilha,
          { signal },
        );
        return { disponivel: true, formulario };
      } catch (causa) {
        if (causa instanceof ErroDaApi && causa.status === 404) {
          return { disponivel: false, motivo: causa.message };
        }
        throw causa;
      }
    },
    enabled: chave.length > 0,
    retry: false,
  });
}

export function chaveDaRevisao(implantacaoId: string) {
  return ["kickoff", "revisao", implantacaoId] as const;
}

/** A revisão de UMA implantação, para quem tem `kickoff:revisar`. */
export function useRevisaoDoKickoff(implantacaoId: string) {
  return useQuery<RevisaoDoKickoff>({
    queryKey: chaveDaRevisao(implantacaoId),
    queryFn: ({ signal }) =>
      pedirJson(
        `/implantacoes/${encodeURIComponent(implantacaoId)}/kickoff`,
        esquemaRevisaoDoKickoff,
        { signal },
      ),
    enabled: implantacaoId.length > 0,
    retry: false,
  });
}

/** O parâmetro da URL que guarda a aba aberta na revisão (spec de 28/09, 6.1). */
export const PARAMETRO_DA_TRILHA = "trilha";

/**
 * O caminho da revisão interna de uma implantação. Um lugar só, para as telas apontarem.
 *
 * Com `alvo`, o link cai na aba da trilha (`?trilha=`) e na pergunta (`#pergunta-…`, a mesma
 * âncora que `PerguntaNoFormulario` declara). A revisão também acha a aba só pela pergunta, então
 * um link antigo com âncora e sem `?trilha=` continua caindo no lugar certo.
 */
export function caminhoDaRevisao(
  implantacaoId: string,
  alvo: { trilha?: string | null; pergunta?: string | null } = {},
): string {
  const base = `/admin/implantacoes/${encodeURIComponent(implantacaoId)}/kickoff`;
  const busca = alvo.trilha
    ? `?${new URLSearchParams({ [PARAMETRO_DA_TRILHA]: alvo.trilha }).toString()}`
    : "";
  const ancora = alvo.pergunta ? `#${ancoraDaPergunta(alvo.pergunta)}` : "";
  return `${base}${busca}${ancora}`;
}

/**
 * A ordem das abas da revisão — a que a consultoria pediu (spec de 28/09, 6.1): Cadastro base ·
 * Infraestrutura · Analytics · Força de Vendas · B2B · Estoque · CRM. A FVT é a variante de
 * malharia do Força de Vendas e fica ao lado dele. Trilha fora da lista vai para o fim, na ordem
 * em que o servidor a mandou — trilha nova aparece em vez de sumir.
 *
 * É só a ordem das ABAS. A lista do cliente segue a ordem do servidor
 * (`ORDEM_DE_EXIBICAO_DAS_TRILHAS`), que é outra decisão e continua valendo lá.
 */
export const ORDEM_DAS_ABAS_DA_REVISAO: readonly string[] = [
  "base",
  "infraestrutura",
  "analytics",
  "forca_de_vendas",
  "fvt",
  "b2b",
  "estoque",
  "crm_360",
];

export function ordenarParaAsAbas<T extends { chave: string }>(trilhas: readonly T[]): T[] {
  const posicao = (chave: string) => {
    const indice = ORDEM_DAS_ABAS_DA_REVISAO.indexOf(chave);
    return indice === -1 ? ORDEM_DAS_ABAS_DA_REVISAO.length : indice;
  };
  // `sort` é estável: as trilhas fora da lista mantêm a ordem do servidor entre si.
  return [...trilhas].sort((a, b) => posicao(a.chave) - posicao(b.chave));
}

/**
 * O nome da trilha no console. A infraestrutura não é uma trilha do kickoff: é o pré-requisito
 * da liberação do ambiente (nota de modelagem da spec de 28/09, 6.3). Onde o console a lista ao
 * lado das trilhas, ela se chama pelo que é.
 */
export function nomeDaTrilhaNoConsole(trilha: { chave: string; nome: string }): string {
  return trilha.chave === TRILHA_DE_INFRAESTRUTURA ? "Questionário de infraestrutura" : trilha.nome;
}

/**
 * A procedência da trilha como o console a diz.
 *
 * O servidor (`questionario/servico.py::_origem_da_trilha`) escreve "Derivada do módulo X, Y." —
 * "derivada" é o nome do escopo no catálogo, não o que a consultora precisa entender. No console
 * a frase vira "Vem junto com os módulos X e Y." (spec 4.4). A tela do cliente continua com o
 * texto do servidor. Frase que não casar passa como veio.
 */
export function origemDaTrilhaNoConsole(origem: string): string {
  const PREFIXO = "Derivada do módulo ";
  if (origem.startsWith(PREFIXO) && origem.endsWith(".")) {
    const nomes = origem.slice(PREFIXO.length, -1).split(", ").filter(Boolean);
    if (nomes.length === 1) return `Vem junto com o módulo ${nomes[0]}.`;
    if (nomes.length > 1) {
      return `Vem junto com os módulos ${nomes.slice(0, -1).join(", ")} e ${nomes.at(-1)}.`;
    }
  }
  if (origem === "Derivada do que já foi informado sobre a empresa.") {
    return "Vem do que já foi informado sobre a empresa.";
  }
  return origem;
}

export const ROTULO_DA_CONTRADICAO: Record<ContradicaoEntreTrilhas["estado"], string> = {
  divergente: "Respostas diferentes",
  /* Tipos diferentes nos dois lados (o FV pergunta em três níveis, o B2B em dois): o portal
     **não afirma** contradição — mostra os dois valores e deixa a leitura com quem revisa. */
  nao_comparavel: "Formatos diferentes — confira",
};

/* Guardar a resposta da mutação no cache, em vez de refazer o GET, não é economia de rede: o
   corpo devolvido pelo PUT é o formulário INTEIRO reavaliado, e é o único estado em que
   condicional, progresso, sugestão de herança e impedimento concordam entre si. Um refetch
   posterior traria o mesmo — mas com uma janela em que a tela mostra o formulário antigo. */
function guardarFormulario(
  cliente: QueryClient,
  chave: string,
  formulario: FormularioDaTrilha,
): void {
  const disponivel: ResultadoDaTrilha = { disponivel: true, formulario };
  cliente.setQueryData<ResultadoDaTrilha>(chaveDaTrilha(chave), disponivel);
  // O progresso e o estado da trilha mudaram: a lista precisa refletir isso quando a pessoa voltar.
  void cliente.invalidateQueries({ queryKey: CHAVE_DAS_TRILHAS });
}

export function useSalvarRascunho(chave: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (respostas: RespostaParaGravar[]) =>
      atualizarJson(caminhoDaApi(chave, "/respostas"), esquemaFormularioDaTrilha, {
        respostas,
      }),
    onSuccess: (formulario) => guardarFormulario(cliente, chave, formulario),
  });
}

export function useEnviarTrilha(chave: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: () =>
      enviarJson(caminhoDaApi(chave, "/enviar"), esquemaFormularioDaTrilha, undefined),
    onSuccess: (formulario) => guardarFormulario(cliente, chave, formulario),
  });
}

export function useAnexar(chave: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ perguntaId, arquivo }: { perguntaId: string; arquivo: File }) => {
      const formulario = new FormData();
      formulario.append("pergunta_id", perguntaId);
      formulario.append("arquivo", arquivo);
      return enviarFormulario(caminhoDaApi(chave, "/anexos"), esquemaAnexo, formulario);
    },
    // O envio devolve só o anexo; quem sabe montar a pergunta com ele é o servidor.
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaveDaTrilha(chave) });
      void cliente.invalidateQueries({ queryKey: CHAVE_DAS_TRILHAS });
    },
  });
}

export function useRemoverAnexo(chave: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (anexoId: string) =>
      apagar(caminhoDaApi(chave, `/anexos/${encodeURIComponent(anexoId)}`)),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaveDaTrilha(chave) });
      void cliente.invalidateQueries({ queryKey: CHAVE_DAS_TRILHAS });
    },
  });
}

/**
 * Endereço de download de um anexo.
 *
 * Sem a chave da trilha no caminho de propósito: quem revisa é interno e não tem organização,
 * então a fronteira aqui é a política de linha do banco, não o parâmetro da URL. O arquivo é
 * sempre servido como binário — nada é renderizado pelo navegador na origem da API.
 */
export function urlDoAnexo(anexoId: string): string {
  return `${BASE_DA_API}/kickoff/anexos/${encodeURIComponent(anexoId)}`;
}

/* ------------------------------------------------------- a recusa do servidor, por pergunta */

const esquemaDetalheDoRascunho = z.object({
  perguntas: z.record(z.string(), z.array(z.string())),
});

const esquemaDetalheDoEnvio = z.object({
  impedimentos: z.array(esquemaImpedimento),
});

/**
 * A recusa do servidor traduzida em "qual campo tem qual problema".
 *
 * Sem isto o `422` viraria uma faixa vermelha no topo dizendo "algumas respostas não puderam ser
 * gravadas" e a pessoa teria de adivinhar quais. O servidor já manda a lista completa — em
 * `detalhe.perguntas` no rascunho e em `detalhe.impedimentos` no envio —, e as duas formas caem
 * no mesmo mapa aqui para que a tela tenha um caminho só.
 */
export function errosPorPergunta(causa: unknown): Record<string, string> {
  if (!(causa instanceof ErroDaApi)) return {};

  const rascunho = esquemaDetalheDoRascunho.safeParse(causa.detalhe);
  if (rascunho.success) {
    return Object.fromEntries(
      Object.entries(rascunho.data.perguntas).map(([id, problemas]) => [
        id,
        problemas.join(" "),
      ]),
    );
  }

  return Object.fromEntries(
    impedimentosDoErro(causa).map((impedimento) => [
      impedimento.pergunta_id,
      impedimento.detalhe,
    ]),
  );
}

/** Os impedimentos que vieram na recusa do envio, na ordem em que o servidor os listou. */
export function impedimentosDoErro(causa: unknown): ImpedimentoDeEnvio[] {
  if (!(causa instanceof ErroDaApi)) return [];
  const analise = esquemaDetalheDoEnvio.safeParse(causa.detalhe);
  return analise.success ? analise.data.impedimentos : [];
}

/* --------------------------------------------------------------- rótulos e formatação */

/** Onde as telas do motor de formulário vivem. Um lugar só, para o card da esteira apontar. */
export const CAMINHO_DOS_FORMULARIOS = "/app/formularios";

export function caminhoDaTrilha(chave: string): string {
  return `${CAMINHO_DOS_FORMULARIOS}/${encodeURIComponent(chave)}`;
}

export const ROTULO_DO_ESTADO: Record<EstadoDaSessao, string> = {
  nao_iniciada: "Não iniciado",
  em_preenchimento: "Em preenchimento",
  enviada: "Enviado",
};

/**
 * A coluna de status (spec de 28/09, 6.2): "Concluído" é trilha enviada com as obrigatórias
 * respondidas — `progresso.concluida`, decidido pelo servidor —; todo o resto é "Em aberto".
 * Esmeralda é o `ok` do design system; nenhum tom novo.
 */
export function situacaoDaTrilha(progresso: ProgressoDaTrilha): {
  rotulo: "Concluído" | "Em aberto";
  chip: "ok" | "neutro";
} {
  return progresso.concluida
    ? { rotulo: "Concluído", chip: "ok" }
    : { rotulo: "Em aberto", chip: "neutro" };
}

/** A mesma escala de cor do design system — nenhum tom novo inventado para esta tela. */
export const CHIP_DO_ESTADO: Record<EstadoDaSessao, "ok" | "aguardando" | "neutro"> = {
  nao_iniciada: "neutro",
  em_preenchimento: "aguardando",
  enviada: "ok",
};

export const ROTULO_DO_MOTIVO: Record<MotivoDeImpedimento, string> = {
  obrigatoria_sem_resposta: "Falta responder",
  promessa_de_anexo_sem_anexo: "Prometeu anexo e não anexou",
  valor_invalido: "Resposta inválida",
};

/** O que o cliente faz nesta trilha agora — o rótulo do botão que leva a ela. */
export function acaoDaTrilha(trilha: TrilhaDaImplantacao): string {
  if (trilha.sessao.estado === "enviada") return "Ver respostas";
  if (!trilha.pode_responder) return "Ver formulário";
  return trilha.sessao.estado === "em_preenchimento" ? "Continuar" : "Começar";
}

const UNIDADES = ["B", "KB", "MB", "GB"] as const;

/** `1536` → `1,5 KB`. Base 1024, que é a que o teto do anexo usa no servidor. */
export function formatarTamanho(bytes: number): string {
  let valor = Math.max(0, bytes);
  let indice = 0;
  while (valor >= 1024 && indice < UNIDADES.length - 1) {
    valor /= 1024;
    indice += 1;
  }
  const casas = valor < 10 && indice > 0 ? 1 : 0;
  return `${valor.toLocaleString("pt-BR", { maximumFractionDigits: casas })} ${UNIDADES[indice]}`;
}

/** Os formatos que a pergunta aceita, no formato que o `accept` do input entende. */
export function formatosAceitos(config: ConfigDaPergunta): string | undefined {
  if (!config.formatos || config.formatos.length === 0) return undefined;
  return config.formatos.map((formato) => `.${formato.replace(/^\./, "")}`).join(",");
}
